import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getVersions, createVersion, cloneVersion, updateVersion, deleteVersion } from '../../../api/autoEstimate';
import AdminLayout from '../../../components/admin/AdminLayout';

const VersionList = () => {
    const navigate = useNavigate();
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Create Mode
    const [isCreating, setIsCreating] = useState(false);
    const [newVersionName, setNewVersionName] = useState('');
    const [newVersionLabel, setNewVersionLabel] = useState('');
    const [newVersionDesc, setNewVersionDesc] = useState('');

    // Edit Metadata Mode
    const [editingVersion, setEditingVersion] = useState(null);

    // Clone Modal State
    const [cloneTargetId, setCloneTargetId] = useState(null);
    const [cloneProcessing, setCloneProcessing] = useState(false);

    useEffect(() => {
        loadVersions();
    }, []);

    const loadVersions = async () => {
        try {
            setLoading(true);
            const data = await getVersions();
            setVersions(data);
        } catch (error) {
            console.error('Failed to load versions', error);
            alert('버전 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!newVersionName.trim()) return alert('버전 이름은 필수입니다.');
        if (!newVersionLabel.trim()) return alert('라벨은 필수입니다.');

        try {
            await createVersion({
                name: newVersionName,
                label: newVersionLabel,
                description: newVersionDesc,
                status: 'DRAFT'
            });
            resetForm();
            loadVersions();
        } catch (error) {
            console.error('Failed to create version', error);
            alert('버전 생성 실패');
        }
    };

    const handleUpdateMetadata = async () => {
        const { id, name, label, description, status } = editingVersion;
        try {
            await updateVersion(id, { name, label, description, status });
            setEditingVersion(null);
            loadVersions();
        } catch (error) {
            alert('수정 실패');
        }
    };

    const handleDeleteVersion = async () => {
        if (!editingVersion) return;

        // First confirmation
        if (!window.confirm(`"버전 ${editingVersion.id}: ${editingVersion.name}"을(를) 삭제하시겠습니까?\n\n⚠️ 이 버전에 포함된 모든 질문, 옵션, 가격 요소가 함께 삭제됩니다.`)) {
            return;
        }

        // Second confirmation (final warning)
        if (!window.confirm('⚠️ 정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다!')) {
            return;
        }

        try {
            await deleteVersion(editingVersion.id);
            alert('버전이 삭제되었습니다.');
            setEditingVersion(null);
            loadVersions();
        } catch (error) {
            console.error('Delete failed', error);
            alert('삭제 실패: ' + (error.response?.data?.message || error.message));
        }
    };

    const executeClone = async () => {
        if (!cloneTargetId) return;
        try {
            setCloneProcessing(true);
            await cloneVersion(cloneTargetId);
            alert('버전이 복제되었습니다.');
            setCloneTargetId(null);
            loadVersions();
        } catch (error) {
            console.error('Clone failed', error);
            alert('복제 실패');
        } finally {
            setCloneProcessing(false);
        }
    };

    const resetForm = () => {
        setNewVersionName('');
        setNewVersionLabel('');
        setNewVersionDesc('');
        setIsCreating(false);
    };

    // Helper to render stacked dates with labels
    const renderDates = (v) => {
        return (
            <div className="flex flex-col gap-1 text-xs">
                <div title="생성일" className="text-gray-500 line-clamp-1"><span className="mr-1">🆕</span>{new Date(v.createdAt).toLocaleDateString()}</div>
                <div title="수정일" className="text-gray-500 line-clamp-1"><span className="mr-1">✍️</span>{v.updatedAt ? new Date(v.updatedAt).toLocaleDateString() : '-'}</div>
                <div title="게시일" className={`font-medium line-clamp-1 ${v.publishedAt ? 'text-blue-600' : 'text-gray-400'}`}>
                    <span className="mr-1">🚀</span>{v.publishedAt ? new Date(v.publishedAt).toLocaleString() : '-'}
                </div>
            </div>
        );
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'ACTIVE': return 'bg-green-100 text-green-800 border-green-200';
            case 'INACTIVE': return 'bg-gray-100 text-gray-800 border-gray-200';
            case 'DRAFT': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    return (
        <AdminLayout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900">견적 버전 관리</h1>
                    <button 
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors" 
                        onClick={() => setIsCreating(true)}
                    >
                        + 새 버전 만들기
                    </button>
                </header>

                {/* Create Modal/Form */}
                {(isCreating || editingVersion) && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => { resetForm(); setEditingVersion(null); }}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            <div className="relative inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start w-full">
                                        <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-title">
                                                {editingVersion ? '버전 정보 수정' : '새 버전 만들기'}
                                            </h3>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">관리용 버전명 (내부 식별용)</label>
                                                    <input
                                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                                                        placeholder="예: 2024 V1"
                                                        value={editingVersion ? editingVersion.name : newVersionName}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            editingVersion ? setEditingVersion({ ...editingVersion, name: val }) : setNewVersionName(val);
                                                        }}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">라벨 (고객에게 노출되는 이름)</label>
                                                    <input
                                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                                                        placeholder="예: 웹사이트 개발 견적"
                                                        value={editingVersion ? editingVersion.label : newVersionLabel}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            editingVersion ? setEditingVersion({ ...editingVersion, label: val }) : setNewVersionLabel(val);
                                                        }}
                                                    />
                                                </div>

                                                {editingVersion && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">상태 (Status)</label>
                                                        <select
                                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border bg-white"
                                                            value={editingVersion.status}
                                                            onChange={e => setEditingVersion({ ...editingVersion, status: e.target.value })}
                                                        >
                                                            <option value="DRAFT">DRAFT (작성 중)</option>
                                                            <option value="ACTIVE">ACTIVE (사용 중 - 고객 노출)</option>
                                                            <option value="INACTIVE">INACTIVE (미사용)</option>
                                                        </select>
                                                    </div>
                                                )}

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">설명 (내부 메모)</label>
                                                    <textarea
                                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border min-h-[80px]"
                                                        placeholder="버전 설명..."
                                                        value={editingVersion ? (editingVersion.description || '') : newVersionDesc}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            editingVersion ? setEditingVersion({ ...editingVersion, description: val }) : setNewVersionDesc(val);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
                                    <button 
                                        type="button" 
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                                        onClick={editingVersion ? handleUpdateMetadata : handleCreate}
                                    >
                                        {editingVersion ? '수정 완료' : '생성'}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        onClick={() => { resetForm(); setEditingVersion(null); }}
                                    >
                                        취소
                                    </button>
                                    {/* Only show delete if original DB status is INACTIVE */}
                                    {editingVersion && versions.find(v => v.id === editingVersion.id)?.status === 'INACTIVE' && (
                                        <button
                                            type="button"
                                            className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-auto sm:w-auto sm:text-sm"
                                            onClick={handleDeleteVersion}
                                        >
                                            삭제
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Clone Confirmation Modal */}
                {cloneTargetId && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setCloneTargetId(null)}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            <div className="relative inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md w-full">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start pl-2">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">버전 복제 확인</h3>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">정말 이 버전을 복제하시겠습니까?</p>
                                                <p className="text-xs text-gray-400 mt-2">
                                                    복제된 버전은 'DRAFT' 상태로 생성되며,<br />
                                                    목록 최상단에 표시됩니다.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
                                    <button 
                                        type="button" 
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                                        onClick={executeClone} 
                                        disabled={cloneProcessing}
                                    >
                                        {cloneProcessing ? '복제 중...' : '확인 (복제)'}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                                        onClick={() => setCloneTargetId(null)} 
                                        disabled={cloneProcessing}
                                    >
                                        취소
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">ID</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">버전 정보</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">상태</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">날짜 (생성/수정/게시)</th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-48">관리</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {versions.map(v => (
                                        <tr key={v.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {v.id}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-gray-900 text-base">{v.label}</div>
                                                <div className="text-sm font-medium text-gray-600 mt-1 flex items-center gap-2">
                                                    <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs">내부명</span>
                                                    {v.name}
                                                </div>
                                                {v.description && (
                                                    <div className="text-sm text-gray-500 mt-2 p-2 bg-gray-50 rounded-md border border-gray-100 italic line-clamp-2">
                                                        "{v.description}"
                                                    </div>
                                                )}
                                                <button 
                                                    className="inline-block mt-2 text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer" 
                                                    onClick={() => setEditingVersion(v)}
                                                >
                                                    ✍️ 정보 수정
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold border ${getStatusStyle(v.status)}`}>
                                                    {v.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {renderDates(v)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end gap-2 flex-col sm:flex-row">
                                                    <button
                                                        className="inline-flex items-center justify-center px-3 py-1.5 border border-blue-600 text-xs font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 transition-colors"
                                                        onClick={() => navigate(`/admin/auto-estimate/${v.id}`)}
                                                    >
                                                        질문 편집
                                                    </button>
                                                    <button
                                                        className="inline-flex items-center justify-center px-3 py-1.5 border border-indigo-200 text-xs font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                                                        onClick={() => setCloneTargetId(v.id)}
                                                    >
                                                        복제
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {versions.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center text-sm text-gray-500">
                                                등록된 버전이 없습니다.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default VersionList;
